import { useState, useRef } from 'react';
import { extractHTMLFromJSON } from '../utils/sseParser';

/**
 * Custom hook for managing streaming content generation
 * 
 * @param {Function} generateFunction - Function to call for content generation
 * @returns {Object} - Hook state and functions
 */
const useStreamingContent = (generateFunction) => {
  // Streaming states
  const [isGenerating, setIsGenerating] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [hasFirstChunk, setHasFirstChunk] = useState(false);
  const [isStreamingDone, setIsStreamingDone] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [finalContent, setFinalContent] = useState("");
  const [parsedHTMLContent, setParsedHTMLContent] = useState("");
  const [error, setError] = useState(null);
  
  // Progress tracking
  const [chunkCount, setChunkCount] = useState(0);
  const [lastChunkTime, setLastChunkTime] = useState(null);
  const [streamingStartTime, setStreamingStartTime] = useState(null);
  
  const streamingTimerRef = useRef(null);

  /**
   * Generate content with streaming
   * @param {Object} formData - Data to send for generation
   * @returns {Promise} - Promise that resolves when generation is complete
   */
  const generateContent = async (formData) => {
    // Reset states
    setIsGenerating(true);
    setIsStreaming(true);
    setHasFirstChunk(false);
    setIsStreamingDone(false);
    setStreamingContent("");
    setFinalContent("");
    setParsedHTMLContent("");
    setError(null);
    
    // Reset progress tracking
    setChunkCount(0);
    setLastChunkTime(null);
    setStreamingStartTime(Date.now());

    // Clear any existing timer
    if (streamingTimerRef.current) {
      clearInterval(streamingTimerRef.current);
    }

    try {
      await generateFunction(
        formData,
        // onChunk callback
        (chunk, accumulatedContent) => {
          if (!hasFirstChunk) {
            setHasFirstChunk(true);
          }
          
          // Track chunk progress
          setChunkCount(prev => prev + 1);
          setLastChunkTime(Date.now());
          
          // Set the streaming content for debugging (only the raw chunks)
          setStreamingContent(prev => prev + chunk);
          
          // Always use accumulated content for display, not individual chunks
          if (accumulatedContent && typeof accumulatedContent === 'string') {
            // Clean the accumulated content
            let cleanedContent = accumulatedContent;
            
            // Remove JSON artifacts if present
            if (cleanedContent.includes('"content":')) {
              const contentMatch = cleanedContent.match(/"content":\s*"([\s\S]*?)"\s*(,|\})/);
              if (contentMatch) {
                cleanedContent = contentMatch[1].replace(/\\"/g, '"').replace(/\\n/g, '\n');
              }
            }
            
            // Remove metafields JSON artifacts
            // cleanedContent = cleanedContent
            //   .replace(/",\s*"metafields":\s*\}\s*$/, '') // Remove metafields JSON artifact
            //   .replace(/,\s*"metafields":\s*\}\s*$/, '') // Remove metafields JSON artifact (without quotes)
            //   .replace(/",\s*"metafields":\s*\}\s*/, '') // Remove metafields JSON artifact anywhere
            //   .replace(/,\s*"metafields":\s*\}\s*/, '') // Remove metafields JSON artifact anywhere (without quotes)
            //   .replace(/\s+$/, '') // Remove trailing whitespace
            //   .trim();
            
            // Check if the content looks like HTML (contains HTML tags)
            const isHTML = /<[^>]+>/.test(cleanedContent);
            // Check if it's raw JSON (starts with { or [)
            const isRawJSON = cleanedContent.trim().startsWith('{') || cleanedContent.trim().startsWith('[');
            
            // Set parsed HTML content if it's HTML and not raw JSON
            if (isHTML && !isRawJSON) {
              setParsedHTMLContent(cleanedContent);
            } else if (isRawJSON) {
              // Try to extract HTML content from raw JSON
              const htmlMatch = cleanedContent.match(/"html_content"\s*:\s*"([\s\S]*?)"\s*(,|\})/);
              if (htmlMatch) {
                const htmlContent = htmlMatch[1].replace(/\\"/g, '"').replace(/\\n/g, '\n');
                setParsedHTMLContent(htmlContent);
              } else {
                // If no HTML content found in JSON, show the raw content for debugging
                setParsedHTMLContent(cleanedContent);
              }
            } else {
              // For non-HTML content, show it as-is for streaming preview
              setParsedHTMLContent(cleanedContent);
            }
          }
        },
        // onComplete callback
        (finalResult) => {
          setIsStreaming(false);
          setIsStreamingDone(true);
          setFinalContent(finalResult.generatedContent);
          
          // Extract HTML content from the final result
          let htmlContent = finalResult.generatedContent;
          
          // If the generated content is a JSON string, try to parse it
          if (typeof htmlContent === 'string' && htmlContent.trim().startsWith('{')) {
            try {
              const parsed = JSON.parse(htmlContent);
              if (parsed.html_content) {
                htmlContent = parsed.html_content;
              } else if (parsed.content) {
                htmlContent = parsed.content;
              }
            } catch (parseError) {
              console.warn('Failed to parse final JSON content:', parseError);
              // Keep original content if parsing fails
            }
          }
          
          // Only set the final parsed HTML content if it's clean HTML
          if (typeof htmlContent === 'string') {
            const isHTML = /<[^>]+>/.test(htmlContent);
            const isRawJSON = htmlContent.trim().startsWith('{') || htmlContent.trim().startsWith('[');
            
            if (isHTML && !isRawJSON) {
              setParsedHTMLContent(htmlContent);
            } else if (isRawJSON) {
              // Try to extract HTML content from raw JSON
              const htmlMatch = htmlContent.match(/"html_content"\s*:\s*"([\s\S]*?)"\s*(,|\})/);
              if (htmlMatch) {
                const extractedHtml = htmlMatch[1].replace(/\\"/g, '"').replace(/\\n/g, '\n');
                setParsedHTMLContent(extractedHtml);
              } else {
                // If no HTML content found, try to extract HTML from the content
                const htmlMatch2 = htmlContent.match(/<[^>]+>.*<\/[^>]+>/s);
                if (htmlMatch2) {
                  setParsedHTMLContent(htmlMatch2[0]);
                }
              }
            } else {
              // If it's not clean HTML, try to extract HTML from the content
              const htmlMatch = htmlContent.match(/<[^>]+>.*<\/[^>]+>/s);
              if (htmlMatch) {
                setParsedHTMLContent(htmlMatch[0]);
              }
            }
          }
          
          setIsGenerating(false);
        },
        // onError callback
        (error) => {
          setError(error.message || 'An error occurred during generation');
          setIsStreaming(false);
          setIsStreamingDone(true);
          setIsGenerating(false);
        }
      );
    } catch (error) {
      setError(error.message || 'An error occurred during generation');
      setIsStreaming(false);
      setIsStreamingDone(true);
      setIsGenerating(false);
    }
  };

  /**
   * Reset all states
   */
  const resetContent = () => {
    setIsGenerating(false);
    setIsStreaming(false);
    setHasFirstChunk(false);
    setIsStreamingDone(false);
    setStreamingContent("");
    setFinalContent("");
    setParsedHTMLContent("");
    setError(null);
    
    // Reset progress tracking
    setChunkCount(0);
    setLastChunkTime(null);
    setStreamingStartTime(null);
  };

  /**
   * Clear error state
   */
  const clearError = () => {
    setError(null);
  };

  return {
    // States
    isGenerating,
    isStreaming,
    hasFirstChunk,
    isStreamingDone,
    streamingContent,
    finalContent,
    parsedHTMLContent,
    error,
    
    // Progress tracking
    chunkCount,
    lastChunkTime,
    streamingStartTime,
    
    // Functions
    generateContent,
    resetContent,
    clearError,
  };
};

export default useStreamingContent;
